import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import ProfileSettingsModal from './ProfileSettingsModal';
import { useAuth } from '../../contexts/AuthContext';
import { getUserNotifications, getUnseenNotificationCount } from '../../services/notificationService';
import DocumentManagementModal from './DocumentManagementModal';

const Header = ({ userRole = 'student', userName = 'John Doe', notifications = 0, customMenu = null, teamId = null }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const profileRef = useRef(null);

  // Helper function to get initials from full name
  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name?.trim()?.split(' ');
    if (names?.length === 1) {
      return names?.[0]?.[0]?.toUpperCase() || 'U';
    }
    return (names?.[0]?.[0] + names?.[names?.length - 1]?.[0])?.toUpperCase() || 'U';
  };

  const loadUnseenCount = async () => {
    if (!user?.id) return;

    try {
      const { success, count } = await getUnseenNotificationCount(user?.id);
      if (success) {
        setUnseenCount(count);
      }
    } catch (err) {
      console.error('Error loading unseen count:', err);
    }
  };

  const loadRecentNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoadingNotifications(true);
      const { success, data } = await getUserNotifications(user?.id, 5);

      if (success) {
        setRecentNotifications(data || []);
      }
    } catch (err) {
      console.error('Error loading recent notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: `/${userRole}-dashboard`, 
      icon: 'LayoutDashboard',
      roles: ['city-official', 'student', 'pcf-admin']
    },
    { 
      label: 'Problems', 
      path: '/problem-statement-browse', 
      icon: 'FileText',
      roles: ['city-official', 'student', 'pcf-admin']
    },
    { 
      label: 'Teams', 
      path: '/team-formation-hub', 
      icon: 'Users',
      roles: ['student', 'pcf-admin']
    },
    { 
      label: 'Learning', 
      path: '/learning-pathway-portal', 
      icon: 'BookOpen',
      roles: ['student', 'pcf-admin']
    },
    { 
      label: 'Messages', 
      path: '/communication-hub', 
      icon: 'MessageSquare',
      roles: ['city-official', 'student', 'pcf-admin']
    }
  ];

  const filteredNavigation = navigationItems?.filter(item => 
    item?.roles?.includes(userRole)
  );

  const profileMenuItems = [
    { label: 'Profile Settings', icon: 'User', action: () => {
      setShowProfileModal(true);
      setIsProfileOpen(false);
    }},
    ...(userRole === 'pcf-admin' || userRole === 'city-official' ? [
      { label: 'Manage Documents', icon: 'FileText', action: () => {
        setShowDocumentModal(true);
        setIsProfileOpen(false);
      }}
    ] : []),
    { label: 'Sign Out', icon: 'LogOut', action: () => navigate('/user-login') }
  ];

  const mockNotifications = [
    { id: 1, title: 'New team invitation', time: '5 min ago', type: 'team' },
    { id: 2, title: 'Problem statement approved', time: '1 hour ago', type: 'success' },
    { id: 3, title: 'Deadline reminder', time: '2 hours ago', type: 'warning' }
  ];

  // Load unseen notification count on component mount
  useEffect(() => {
    if (user?.id) {
      loadUnseenCount();
      // Refresh count every 30 seconds
      const interval = setInterval(loadUnseenCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    if (path?.includes('dashboard')) {
      return location?.pathname?.includes('dashboard');
    }
    return location?.pathname === path || location?.pathname?.startsWith(path);
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'city-official': 'City Official',
      'student': 'Student',
      'pcf-admin': 'PCF Admin'
    };
    return roleMap?.[role] || 'User';
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full bg-card border-b border-border shadow-soft">
        <div className="flex h-16 items-center px-3 sm:px-4 lg:px-6">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
              <Icon name="Building2" size={20} color="white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-base sm:text-lg font-semibold text-foreground">Smart Cities</h1>
              <p className="text-xs text-muted-foreground -mt-1">Onboarding Platform</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 ml-6 lg:ml-8">
            {(customMenu || filteredNavigation)?.map((item) => (
              <Button
                key={item?.id || item?.path}
                variant={
                  customMenu
                    ? (item?.active ? "default" : "ghost")
                    : (isActivePath(item?.path) ? "default" : "ghost")
                }
                size="sm"
                onClick={() => {
                  if (customMenu) item?.onClick?.();
                  else handleNavigation(item?.path);
                }}
                iconName={item?.icon}
                iconPosition="left"
                iconSize={16}
                className="transition-micro"
              >
                {item?.label}
              </Button>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-1 sm:space-x-2 ml-auto">
            {/* Profile Menu with Circular Initials Avatar */}
            <div className="relative" ref={profileRef}>
              <Button
                variant="ghost"
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-1 sm:space-x-2 transition-micro px-2 sm:px-3"
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  {/* Circular Avatar with Initials */}
                  <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary text-white font-medium text-xs sm:text-sm">
                    {getInitials(userName)}
                  </div>
                  <Icon name="ChevronDown" size={14} className="sm:w-4 sm:h-4" />
                </div>
              </Button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-52 sm:w-56 bg-popover border border-border rounded-lg shadow-elevated animate-fade-in">
                  <div className="p-3 border-b border-border">
                    <p className="font-medium text-sm sm:text-base text-popover-foreground truncate">{userName}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">{getRoleDisplayName(userRole)}</p>
                  </div>
                  <div className="py-1">
                    {profileMenuItems?.map((item, index) => (
                      <button
                        key={index}
                        onClick={item?.action}
                        className="w-full flex items-center space-x-3 px-3 py-2 text-xs sm:text-sm text-popover-foreground hover:bg-muted transition-micro"
                      >
                        <Icon name={item?.icon} size={16} />
                        <span>{item?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden transition-micro h-9 w-9 sm:h-10 sm:w-10"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-card animate-slide-in">
            <nav className="px-3 sm:px-4 py-2 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
              {(customMenu || filteredNavigation)?.map((item) => (
                <Button
                  key={item?.id || item?.path}
                  variant={
                    customMenu
                      ? (item?.active ? "default" : "ghost")
                      : (isActivePath(item?.path) ? "default" : "ghost")
                  }
                  size="sm"
                  onClick={() => {
                    if (customMenu) item?.onClick?.();
                    else handleNavigation(item?.path);
                  }}
                  iconName={item?.icon}
                  iconPosition="left"
                  iconSize={16}
                  className="w-full justify-start transition-micro"
                >
                  {item?.label}
                </Button>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Profile Settings Modal */}
      <ProfileSettingsModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />

      {/* Document Management Modal */}
      <DocumentManagementModal 
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
      />
    </>
  );
};

export default Header;